import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Trash2, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, BarChart3, Globe, Share2, ChevronLeft, ChevronRight, Key, Edit2, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import UpgradeModal from '../components/UpgradeModal';
import DomainSelector from '../components/DomainSelector';
import PostKeywordsModal from '../components/PostKeywordsModal';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_PER_PAGE = 10;
const PER_PAGE_OPTIONS = [10, 25, 50, 100, 200];

const SOURCE_COLORS = {
  'Facebook': '#1877f2',
  'Google': '#4285f4',
  'Direct': '#6b7280',
  'Twitter/X': '#1da1f2',
  'Instagram': '#e4405f',
  'Pinterest': '#bd081c',
  'LinkedIn': '#0077b5',
  'YouTube': '#ff0000',
  'Bit.ly': '#ee6123',
  'Reddit': '#ff4500',
  'TikTok': '#000000',
  'Bing': '#008373',
  'Other': '#9ca3af',
};

const PostsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const userPlan = user?.subscription?.plan || 'free';
  const isFreePlan = userPlan === 'free';
  
  // Domain selection state
  const [selectedDomain, setSelectedDomain] = useState(null);
  
  // AI Features state
  const [aiFeatures, setAiFeatures] = useState({ post_editing_enabled: false, ai_optimization_enabled: false });
  
  // Data state
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [sortBy, setSortBy] = useState('sessions');
  const [sortOrder, setSortOrder] = useState('desc');
  const [noConfigError, setNoConfigError] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage, setPostsPerPage] = useState(DEFAULT_PER_PAGE);
  const [totalPosts, setTotalPosts] = useState(0);
  
  // Loading/Cache state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  
  // Modal state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeError, setUpgradeError] = useState(null);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 });
  
  // Post Analysis Modal state
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
  const [analyzingPost, setAnalyzingPost] = useState(null);
  const [postAnalytics, setPostAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Post Keywords Modal state (GSC)
  const [showKeywordsModal, setShowKeywordsModal] = useState(false);
  const [keywordsPost, setKeywordsPost] = useState(null);
  
  // Polling interval for refresh status
  const pollIntervalRef = useRef(null);

  // Calculate total pages
  const totalPages = Math.ceil(totalPosts / postsPerPage);

  // Fetch AI features status
  useEffect(() => {
    const fetchAiFeatures = async () => {
      try {
        const response = await axios.get(`${API}/ai/features/status`);
        setAiFeatures(response.data);
      } catch (error) {
        console.error('Failed to fetch AI features status');
      }
    };
    fetchAiFeatures();
  }, []);

  // Handle domain change
  const handleDomainChange = (domainId) => {
    if (domainId === selectedDomain) return;
    
    setSelectedDomain(domainId);
    setSelectedPosts([]);
    setCurrentPage(1);
    setNoConfigError(false);
    setInitialLoadDone(false);
    setPosts([]);
    setFilteredPosts([]);
    setLastUpdated(null);
  };

  // Load cached posts from server
  const loadCachedPosts = useCallback(async () => {
    if (!selectedDomain) return;
    
    try {
      console.log('Loading posts with params:', { dateRange, sortBy, sortOrder, currentPage, postsPerPage });
      
      const response = await axios.get(`${API}/cache/posts/${selectedDomain}`, {
        params: {
          date_range: dateRange,
          page: currentPage,
          per_page: postsPerPage,
          sort_by: sortBy,
          sort_order: sortOrder,
          _t: Date.now() // Cache bust
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('API Response:', response.data.total, 'posts, first:', response.data.data?.[0]?.date);

      if (response.data.success) {
        setPosts(response.data.data || []);
        setTotalPosts(response.data.total || 0);
        setLastUpdated(response.data.last_updated);
        setIsRefreshing(response.data.is_refreshing || false);
        setInitialLoadDone(true);
        
        // Start polling if refreshing
        if (response.data.is_refreshing) {
          startPolling();
        }
        
        // Show message if no cached data
        if (!response.data.cached && response.data.message) {
          toast.info(response.data.message);
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setNoConfigError(true);
        toast.error('Please configure your credentials in the Setup page');
      } else {
        toast.error('Failed to load posts: ' + (error.response?.data?.detail || error.message));
      }
    }
  }, [selectedDomain, dateRange, currentPage, postsPerPage, sortBy, sortOrder, token]);

  // Load cached data when domain changes
  useEffect(() => {
    if (!selectedDomain || !token) return;
    loadCachedPosts();
  }, [selectedDomain, token, loadCachedPosts]);

  // Re-apply sorting/pagination when filters change
  useEffect(() => {
    if (!initialLoadDone || !selectedDomain) return;
    loadCachedPosts();
  }, [dateRange, sortBy, sortOrder, currentPage, postsPerPage, initialLoadDone, selectedDomain, loadCachedPosts]);

  // Apply search filter
  useEffect(() => {
    applySearchFilter();
  }, [posts, searchQuery]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Trigger refresh and start polling
  const triggerRefresh = async () => {
    if (!selectedDomain || isRefreshing) return;
    
    setIsRefreshing(true);
    
    try {
      const response = await axios.post(
        `${API}/cache/refresh/posts/${selectedDomain}`,
        { date_range: dateRange },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.info('Refreshing data in background...');
        startPolling();
      }
    } catch (error) {
      setIsRefreshing(false);
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (detail?.error === 'limit_exceeded') {
          setUpgradeError(detail);
          setShowUpgradeModal(true);
        } else {
          toast.error('Access denied. Please upgrade your plan.');
        }
      } else {
        toast.error('Failed to refresh: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  // Poll for refresh completion
  const startPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/cache/posts/${selectedDomain}`, {
          params: {
            date_range: dateRange,
            page: currentPage,
            per_page: postsPerPage,
            sort_by: sortBy,
            sort_order: sortOrder
          },
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          // Update data
          setPosts(response.data.data || []);
          setTotalPosts(response.data.total || 0);
          setLastUpdated(response.data.last_updated);
          
          // Check if refresh is complete
          if (!response.data.is_refreshing) {
            setIsRefreshing(false);
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
            toast.success('Data refreshed successfully!');
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000); // Poll every 3 seconds
  };

  const applySearchFilter = () => {
    let filtered = [...posts];

    if (searchQuery) {
      filtered = filtered.filter(
        (post) =>
          post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.link?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredPosts(filtered);
  };

  const toggleSort = (column) => {
    if (isFreePlan) {
      toast.error('Upgrade to Pro or Lifetime to enable column sorting');
      return;
    }
    
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const togglePostSelection = (postId) => {
    setSelectedPosts((prev) =>
      prev.includes(postId) ? prev.filter((id) => id !== postId) : [...prev, postId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPosts.length === filteredPosts.length) {
      setSelectedPosts([]);
    } else {
      setSelectedPosts(filteredPosts.map((post) => post.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedDomain) {
      toast.error('Please select a domain first');
      return;
    }
    
    if (selectedPosts.length === 0) {
      toast.error('No posts selected');
      return;
    }
    
    setDeleting(true);
    const total = selectedPosts.length;
    setDeleteProgress({ current: 0, total, success: 0, failed: 0 });
    
    try {
      const requestBody = {
        post_ids: selectedPosts,
        force: true,
        config_id: selectedDomain,
      };
      
      const response = await axios.post(`${API}/wordpress/delete`, requestBody, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const successCount = response.data.success?.length || 0;
      const failedCount = response.data.failed?.length || 0;
      const mediaDeleted = response.data.total_media_deleted || 0;
      
      setDeleteProgress({
        current: total,
        total,
        success: successCount,
        failed: failedCount
      });

      if (successCount > 0) {
        const mediaMsg = mediaDeleted > 0 ? ` and ${mediaDeleted} media attachment(s)` : '';
        toast.success(`Deleted ${successCount} post(s)${mediaMsg}`, { duration: 5000 });
        
        const deletedIds = new Set(response.data.success || []);
        setPosts(prev => prev.filter(p => !deletedIds.has(p.id)));
        setFilteredPosts(prev => prev.filter(p => !deletedIds.has(p.id)));
      }
      
      if (failedCount > 0) {
        toast.error(`Failed to delete ${failedCount} post(s)`);
      }

      setSelectedPosts([]);
      
    } catch (error) {
      if (error.response?.status === 403) {
        const detail = error.response.data?.detail;
        if (detail?.error === 'limit_exceeded') {
          setUpgradeError(detail);
          setShowUpgradeModal(true);
        } else {
          toast.error('Bulk delete limit exceeded. Please upgrade your plan.');
        }
      } else {
        toast.error('Delete failed: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setDeleteProgress({ current: 0, total: 0, success: 0, failed: 0 });
    }
  };

  // Analyze a single post
  const handleAnalyzePost = async (post) => {
    setAnalyzingPost(post);
    setShowAnalyzeModal(true);
    setLoadingAnalytics(true);
    setPostAnalytics(null);
    
    try {
      const startDate = dateRange === 'all' 
        ? '2015-08-14' 
        : new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const endDate = new Date().toISOString().split('T')[0];
      
      const response = await axios.get(`${API}/analytics/post/${post.slug}`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          config_id: selectedDomain,
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPostAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to load analytics: ' + (error.response?.data?.detail || error.message));
      setShowAnalyzeModal(false);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  // Format last updated time
  const formatLastUpdated = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    return date.toLocaleString();
  };

  return (
    <div>
      <div className="page-header">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground" data-testid="posts-title">
              Posts Management
            </h1>
            <p className="text-slate-600 mt-2">
              View and manage your WordPress posts with traffic data
            </p>
          </div>
          <div className="flex items-center gap-3">
            <DomainSelector onDomainChange={handleDomainChange} />
            <Button
              onClick={triggerRefresh}
              variant="outline"
              data-testid="refresh-posts-btn"
              disabled={isRefreshing || !selectedDomain}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
        
        {/* Last Updated Info */}
        {lastUpdated && (
          <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>Last updated: {formatLastUpdated(lastUpdated)}</span>
            {isRefreshing && <span className="text-blue-600 ml-2">• Updating in background...</span>}
          </div>
        )}
        
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-slate-700">
            <strong>How to use:</strong> Data is refreshed automatically at 12 AM daily. 
            Click "Refresh" to fetch the latest data manually. The page shows cached data instantly.
          </p>
        </div>
      </div>

      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search posts by title or URL..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-posts-input"
                className="pl-10 input-field"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="text-xs text-slate-500 font-medium self-center px-2" title="GA4 traffic data time range">
              Traffic Data:
            </div>
            <Select value={dateRange} onValueChange={(v) => { setDateRange(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-[160px]" data-testid="date-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last 12 months</SelectItem>
                <SelectItem value="all">All (Lifetime)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Active Filters Summary */}
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
          <strong>Current View:</strong> Posts sorted by{' '}
          <span className="text-purple-600 font-medium">
            {sortBy === 'sessions' 
              ? (sortOrder === 'desc' ? 'most viewed' : 'least viewed')
              : (sortOrder === 'desc' ? 'newest' : 'oldest')}
          </span>
          {' '}• Traffic data: <span className="text-blue-600">{dateRange === 'all' ? 'Lifetime (all time)' : `Last ${dateRange} days`}</span>
          {' '}• <span className="text-slate-800">{totalPosts.toLocaleString()} total posts</span>
          {selectedPosts.length > 0 && (
            <span className="ml-2 text-red-600 font-medium">
              • {selectedPosts.length} selected for deletion
            </span>
          )}
        </div>
      </div>

      <Card data-testid="posts-table-card">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <Checkbox
                      checked={selectedPosts.length === filteredPosts.length && filteredPosts.length > 0}
                      onCheckedChange={toggleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Post Title
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => toggleSort('date')}
                    data-testid="sort-published"
                  >
                    <div className="flex items-center gap-2">
                      <span>Published</span>
                      {sortBy === 'date' ? (
                        sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                      ) : (
                        <ArrowUpDown className="w-4 h-4 opacity-40" />
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => toggleSort('sessions')}
                    data-testid="sort-sessions"
                  >
                    <div className="flex items-center gap-2">
                      <span>Sessions</span>
                      {sortBy === 'sessions' ? (
                        sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
                      ) : (
                        <ArrowUpDown className="w-4 h-4 opacity-40" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Page Views
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Users
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredPosts.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="empty-state">
                        {noConfigError ? (
                          <>
                            <p className="text-slate-600 font-medium mb-2">No domain configured</p>
                            <p className="text-slate-500 text-sm">Please go to Setup page to connect your WordPress and Google Analytics</p>
                          </>
                        ) : !initialLoadDone ? (
                          <p className="text-slate-500">Select a domain to view posts</p>
                        ) : (
                          <>
                            <p className="text-slate-500 mb-2">No cached data available</p>
                            <Button onClick={triggerRefresh} disabled={isRefreshing}>
                              <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                              {isRefreshing ? 'Fetching...' : 'Fetch Data'}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredPosts.map((post) => (
                    <tr
                      key={post.id}
                      className="table-hover-row"
                      data-testid={`post-row-${post.id}`}
                    >
                      <td className="px-6 py-4">
                        <Checkbox
                          checked={selectedPosts.includes(post.id)}
                          onCheckedChange={() => togglePostSelection(post.id)}
                          data-testid={`checkbox-post-${post.id}`}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div className="font-medium text-foreground">{post.title}</div>
                          <a
                            href={post.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-accent hover:underline"
                          >
                            {post.link}
                          </a>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">
                        {post.date ? new Date(post.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        }) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {post.sessions?.toLocaleString() || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {post.pageViews?.toLocaleString() || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-900">
                        {post.users?.toLocaleString() || 0}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            post.status === 'publish'
                              ? 'bg-success/10 text-success'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {post.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAnalyzePost(post)}
                            className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                            title="Analyze traffic"
                          >
                            <BarChart3 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setKeywordsPost(post); setShowKeywordsModal(true); }}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            title="View ranking keywords"
                          >
                            <Key className="w-4 h-4" />
                          </Button>
                          {aiFeatures.post_editing_enabled && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/posts/${post.id}/edit${selectedDomain ? `?config_id=${selectedDomain}` : ''}`)}
                              className="text-purple-600 hover:text-purple-800 hover:bg-purple-50"
                              title="Edit post with AI"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination Controls */}
      {totalPosts > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 p-4 bg-white rounded-lg border border-slate-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-600">Show</span>
            <Select value={postsPerPage.toString()} onValueChange={(val) => { setPostsPerPage(Number(val)); setCurrentPage(1); }}>
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PER_PAGE_OPTIONS.map(opt => (
                  <SelectItem key={opt} value={opt.toString()}>{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-sm text-slate-600">per page</span>
          </div>

          <div className="text-sm text-slate-600">
            Showing {((currentPage - 1) * postsPerPage) + 1} - {Math.min(currentPage * postsPerPage, totalPosts)} of {totalPosts.toLocaleString()} posts
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="h-8 px-2"
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="h-8 px-2"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-1">
              {(() => {
                const pages = [];
                const maxVisible = 5;
                let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                let end = Math.min(totalPages, start + maxVisible - 1);
                if (end - start + 1 < maxVisible) {
                  start = Math.max(1, end - maxVisible + 1);
                }
                
                if (start > 1) {
                  pages.push(
                    <Button key={1} variant={currentPage === 1 ? "default" : "outline"} size="sm" onClick={() => setCurrentPage(1)} className="h-8 w-8 p-0">1</Button>
                  );
                  if (start > 2) pages.push(<span key="dots1" className="px-1 text-slate-400">...</span>);
                }
                
                for (let i = start; i <= end; i++) {
                  pages.push(
                    <Button key={i} variant={currentPage === i ? "default" : "outline"} size="sm" onClick={() => setCurrentPage(i)} className="h-8 w-8 p-0">{i}</Button>
                  );
                }
                
                if (end < totalPages) {
                  if (end < totalPages - 1) pages.push(<span key="dots2" className="px-1 text-slate-400">...</span>);
                  pages.push(
                    <Button key={totalPages} variant={currentPage === totalPages ? "default" : "outline"} size="sm" onClick={() => setCurrentPage(totalPages)} className="h-8 w-8 p-0">{totalPages}</Button>
                  );
                }
                
                return pages;
              })()}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="h-8 px-2"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="h-8 px-2"
            >
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Sticky Floating Delete Bar */}
      {selectedPosts.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 duration-200">
          <div className="flex items-center gap-4 px-6 py-3 bg-slate-900 text-white rounded-full shadow-2xl border border-slate-700">
            <span className="text-sm font-medium">
              {selectedPosts.length} post{selectedPosts.length > 1 ? 's' : ''} selected
            </span>
            <div className="w-px h-5 bg-slate-600" />
            <Button
              onClick={() => setSelectedPosts([])}
              variant="ghost"
              size="sm"
              className="text-slate-300 hover:text-white hover:bg-slate-800"
            >
              Clear
            </Button>
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="destructive"
              size="sm"
              className="bg-red-600 hover:bg-red-700"
              data-testid="floating-delete-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Selected
            </Button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-confirmation-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {deleting ? 'Deleting Posts...' : 'Delete Permanently?'}
            </DialogTitle>
            <DialogDescription asChild>
              <div>
                {deleting ? (
                  <div className="space-y-4 pt-2">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-red-500"></div>
                    </div>
                    <div className="text-center">
                      <span className="text-xl font-bold text-slate-800">
                        Deleting {deleteProgress.total} posts...
                      </span>
                      {deleteProgress.current > 0 && (
                        <div className="mt-2">
                          <span className="text-green-600 font-medium">✓ {deleteProgress.success} deleted</span>
                          {deleteProgress.failed > 0 && (
                            <span className="text-red-600 font-medium ml-3">✗ {deleteProgress.failed} failed</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="text-slate-600">
                    You are about to permanently delete {selectedPosts.length} post(s) and their media attachments. 
                    This action cannot be undone.
                  </span>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          {!deleting && (
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)} data-testid="cancel-delete-btn">
                Cancel
              </Button>
              <Button
                onClick={handleBulkDelete}
                variant="destructive"
                data-testid="confirm-delete-btn"
              >
                Delete Permanently
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Upgrade Modal */}
      <UpgradeModal 
        open={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)}
        errorData={upgradeError}
      />

      {/* Post Analytics Modal */}
      <Dialog open={showAnalyzeModal} onOpenChange={setShowAnalyzeModal}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Post Analytics
            </DialogTitle>
          </DialogHeader>
          
          {analyzingPost && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-800 line-clamp-2">{analyzingPost.title}</h3>
                <a 
                  href={analyzingPost.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-purple-600 hover:underline"
                >
                  {analyzingPost.link}
                </a>
                <div className="flex gap-4 mt-2 text-sm text-slate-600">
                  <span>Sessions: <strong>{analyzingPost.sessions?.toLocaleString() || 0}</strong></span>
                  <span>Users: <strong>{analyzingPost.users?.toLocaleString() || 0}</strong></span>
                  <span>Views: <strong>{analyzingPost.pageViews?.toLocaleString() || 0}</strong></span>
                </div>
              </div>
              
              {loadingAnalytics ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                </div>
              ) : postAnalytics ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Traffic by Country */}
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2 text-slate-700">
                      <Globe className="w-4 h-4" />
                      Traffic by Country
                    </h4>
                    {postAnalytics.countries?.length > 0 ? (
                      <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="text-xs text-slate-500 uppercase sticky top-0 bg-slate-50">
                            <tr>
                              <th className="text-left py-1.5 px-2">Country</th>
                              <th className="text-right py-1.5 px-2">Sessions</th>
                              <th className="text-right py-1.5 px-2">Users</th>
                            </tr>
                          </thead>
                          <tbody>
                            {postAnalytics.countries.map((c, i) => {
                              const maxSessions = postAnalytics.countries[0]?.sessions || 1;
                              const barWidth = (c.sessions / maxSessions) * 100;
                              return (
                                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                                  <td className="py-1 px-2">
                                    <div className="flex items-center gap-1.5">
                                      <div 
                                        className="h-1.5 rounded-full bg-purple-500" 
                                        style={{ width: `${Math.max(barWidth, 5)}%`, minWidth: '3px', maxWidth: '40px' }}
                                      />
                                      <span className="text-xs">{c.country}</span>
                                    </div>
                                  </td>
                                  <td className="text-right py-1 px-2 text-xs font-medium">{c.sessions?.toLocaleString()}</td>
                                  <td className="text-right py-1 px-2 text-xs text-slate-600">{c.users?.toLocaleString()}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm py-4">No country data available</p>
                    )}
                  </div>

                  {/* Traffic by Source */}
                  <div className="space-y-3">
                    <h4 className="font-semibold flex items-center gap-2 text-slate-700">
                      <Share2 className="w-4 h-4" />
                      Traffic Sources
                    </h4>
                    {postAnalytics.sources?.length > 0 ? (
                      <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                        <table className="w-full text-sm">
                          <thead className="text-xs text-slate-500 uppercase sticky top-0 bg-slate-50">
                            <tr>
                              <th className="text-left py-1.5 px-2">Source</th>
                              <th className="text-right py-1.5 px-2">Sessions</th>
                              <th className="text-right py-1.5 px-2">Users</th>
                            </tr>
                          </thead>
                          <tbody>
                            {postAnalytics.sources.map((s, i) => {
                              const maxSessions = postAnalytics.sources[0]?.sessions || 1;
                              const barWidth = (s.sessions / maxSessions) * 100;
                              return (
                                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                                  <td className="py-1 px-2">
                                    <div className="flex items-center gap-1.5">
                                      <span 
                                        className="w-2 h-2 rounded-full flex-shrink-0" 
                                        style={{ backgroundColor: SOURCE_COLORS[s.source] || '#9ca3af' }}
                                      />
                                      <div 
                                        className="h-1.5 rounded-full" 
                                        style={{ 
                                          width: `${Math.max(barWidth, 5)}%`, 
                                          minWidth: '3px', 
                                          maxWidth: '35px',
                                          backgroundColor: SOURCE_COLORS[s.source] || '#9ca3af'
                                        }}
                                      />
                                      <span className="text-xs">{s.source}</span>
                                    </div>
                                  </td>
                                  <td className="text-right py-1 px-2 text-xs font-medium">{s.sessions?.toLocaleString()}</td>
                                  <td className="text-right py-1 px-2 text-xs text-slate-600">{s.users?.toLocaleString()}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm py-4">No source data available</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No analytics data available</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Keywords Modal (GSC) */}
      <PostKeywordsModal
        isOpen={showKeywordsModal}
        onClose={() => { setShowKeywordsModal(false); setKeywordsPost(null); }}
        post={keywordsPost}
        token={token}
        configId={selectedDomain}
      />
    </div>
  );
};

export default PostsPage;
